import { useState } from "react";
import { useAuth } from "./AuthContext";

export function UnlockModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { unlock, unlocked, logout } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adminMsg, setAdminMsg] = useState<string | null>(null);

  if (!open) return null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await unlock(pin);
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? "Failed");
      return;
    }
    setPin("");
    onClose();
  }

  async function runAdmin(path: string, label: string) {
    setAdminMsg(null);
    setBusy(true);
    try {
      const res = await fetch(path, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      const data = await res.json();
      if (!res.ok) {
        setAdminMsg(`${label} failed: ${(data as { error?: string }).error ?? res.status}`);
      } else {
        setAdminMsg(`${label} ok`);
      }
    } catch {
      setAdminMsg(`${label} error`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Admin unlock">
      <div className="modal-card">
        <div className="modal-head">
          <div>
            <h2 className="tl-title" style={{ fontSize: "1.1rem" }}>
              {unlocked ? "Admin" : "Unlock"}
              <span className="ctx">
                {unlocked ? "/ session active" : "/ admin PIN"}
              </span>
            </h2>
            {unlocked ? (
              <span className="status-led" style={{ display: "inline-flex", marginTop: "0.45rem" }}>
                Jobs available
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="btn-icon"
            aria-label="Close"
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {!unlocked ? (
          <form onSubmit={onSubmit} className="unlock-form">
            <p className="muted" style={{ margin: 0 }}>
              Enter the admin PIN to refresh prices, rescore, caveats, fal sync, or X caveats.
              The public catalog stays readable.
            </p>
            <label className="sr-only" htmlFor="admin-pin">
              Admin PIN
            </label>
            <input
              id="admin-pin"
              type="password"
              autoComplete="current-password"
              placeholder="PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              disabled={busy}
            />
            {error && <p className="form-error">{error}</p>}
            <div className="yuri-footer-row" style={{ marginTop: "0.35rem", paddingTop: "0.85rem" }}>
              <span className="meta-left">Public catalog stays open</span>
              <div className="cta-row">
                <button type="button" className="btn ghost sm" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn sm" disabled={busy || !pin}>
                  {busy ? "Checking…" : "Unlock"}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="admin-actions">
            <p className="muted" style={{ marginTop: 0 }}>
              Session active. Run maintenance jobs:
            </p>
            <div className="admin-btn-row">
              <button
                type="button"
                className="btn sm"
                disabled={busy}
                onClick={() => void runAdmin("/api/admin/scrape-prices", "Scrape prices")}
              >
                Scrape prices
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={busy}
                onClick={() => void runAdmin("/api/admin/refresh-prices", "Refresh prices")}
              >
                Refresh prices
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={busy}
                onClick={() => void runAdmin("/api/admin/rescore-confidence", "Rescore")}
              >
                Rescore confidence
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={busy}
                onClick={() => void runAdmin("/api/admin/refresh-caveats", "Caveats")}
              >
                Refresh caveats
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={busy}
                onClick={() => void runAdmin("/api/admin/sync-fal", "fal sync")}
              >
                Sync fal
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={busy}
                onClick={() => void runAdmin("/api/admin/refresh-x-caveats", "X caveats")}
              >
                Refresh X caveats
              </button>
            </div>
            <div className="yuri-footer-row">
              <span className="meta-left">{adminMsg ?? "Ready"}</span>
              <div className="cta-row">
                <button type="button" className="btn ghost sm" disabled={busy} onClick={() => void logout()}>
                  Lock
                </button>
                <button type="button" className="btn sm" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
